import type { ReactNode } from 'react';
import { Suspense, use, useEffect, Component } from 'react';
import { classifyLookupFailure, getLookupErrorMessage } from '@plumx/utils';
import type { LookupErrorCode } from '@plumx/utils';
import type { WidgetProps } from './types';
import type { WidgetApiError } from './api';
import { fetchWidgetData, extractMetrics } from './api';
import { hasVisibleMetrics } from './utils/categories';
import { I18nProvider, resolveLanguage, useTranslation } from './i18n/i18n';
import PlumPrint from './components/PlumPrint';
import Popup from './components/Popup';
import Summary from './components/Summary';
import Details from './components/Details';
import Badge from './components/Badge';
import MetricsPanel from './components/MetricsPanel';
import styles from './styles/widget.module.scss';

const Skeleton = () => <span className={styles.skeleton} aria-label="Loading…" />;
const ErrorState = ({ message }: { message: string }) => (
  <span className={styles.error} role="alert">
    {message}
  </span>
);

/**
 * Extracts the numeric HTTP status from a caught error, if any (FR-4.1/FR-4.2).
 * `WidgetApiError` carries `.status`; network/abort errors thrown by `fetch`
 * itself don't. Uses `??`, not a truthiness check, so a real `status: 0`
 * (e.g. an opaque/CORS-blocked response) isn't conflated with "no status".
 */
const getErrorStatus = (error: unknown): number | undefined =>
  (error as WidgetApiError)?.status ?? undefined;

/**
 * Error Boundary to catch errors from async data loading
 * Prevents infinite retry loops by not retrying after catching an error
 */
class WidgetErrorBoundary extends Component<
  { children: ReactNode; onEvent: WidgetProps['onEvent'] },
  { hasError: boolean; code?: LookupErrorCode }
> {
  constructor(props: { children: ReactNode; onEvent: WidgetProps['onEvent'] }) {
    super(props);
    this.state = { hasError: false };
  }

  static getDerivedStateFromError(error: Error) {
    return { hasError: true, code: classifyLookupFailure(getErrorStatus(error)) };
  }

  // Dispatch the host-facing event here, not in render(): render can be
  // double-invoked under StrictMode, which would double-fire the event.
  // componentDidCatch runs once per actual caught error (AR-4.3).
  componentDidCatch(): void {
    if (this.state.code) {
      this.props.onEvent('error', { code: this.state.code });
    }
  }

  render() {
    if (this.state.hasError && this.state.code) {
      return <ErrorState message={getLookupErrorMessage(this.state.code)} />;
    }

    return this.props.children;
  }
}

interface WidgetContentProps extends WidgetProps {
  data: Awaited<ReturnType<typeof fetchWidgetData>>;
}

const WidgetContent = ({
  data,
  config,
  display,
  onEvent,
  identifier,
  identifierType,
  theme,
}: WidgetContentProps) => {
  const { t } = useTranslation();
  const metrics = extractMetrics(data);
  // Empty when no category survives the hide filters (covers "no data" and "all filtered out").
  const isEmpty = !hasVisibleMetrics(metrics, config);

  // Map config.printSize to component sizes
  const sizeMap = {
    tiny: 'xs',
    small: 'sm',
    medium: 'md',
    large: 'lg',
  } as const;

  const getPlumprintSize = (): 'xs' | 'sm' | 'md' | 'lg' => {
    const configSize = config.printSize as keyof typeof sizeMap | undefined;

    // Badge display: only allow 'tiny' override, ignore medium/large (legacy parity)
    if (display === 'badge') {
      return configSize === 'tiny' ? 'xs' : 'sm';
    }

    // Details/Summary always render a fixed, type-appropriate size — a
    // hand-authored config.printSize must have zero effect here (FR-1.1).
    if (display === 'details') return 'lg';
    if (display === 'summary') return 'lg';

    // Plumprint: honor config.printSize, but only for the map's own keys —
    // an own-key-safe lookup so prototype-chain keys (e.g. "constructor")
    // can't leak through (FR-1.2/AR-1.2).
    if (configSize && Object.hasOwn(sizeMap, configSize)) return sizeMap[configSize];
    return 'lg';
  };

  // Fire analytics/host events after render — never during render (StrictMode safety)
  useEffect(() => {
    if (isEmpty) {
      onEvent('empty', { id: data.id });
    } else {
      onEvent('load', { data, metrics });
    }
  }, [isEmpty, data, metrics, onEvent]);

  if (isEmpty) {
    if (config.hideWhenEmpty) return null;
    return <ErrorState message={t('No metrics available.')} />;
  }

  const plumprintSize = getPlumprintSize();

  return (
    <div className={styles.widget}>
      {display === 'plumprint' && (
        <Popup
          trigger={<PlumPrint metrics={metrics} size={plumprintSize} />}
          direction={config.popupDirection}
          theme={theme}
        >
          <MetricsPanel
            metrics={metrics}
            data={data}
            config={config}
            identifier={identifier}
            identifierType={identifierType}
            theme={theme}
          />
        </Popup>
      )}

      {display === 'summary' && (
        <Summary
          metrics={metrics}
          data={data}
          config={config}
          printSize={plumprintSize}
          identifier={identifier}
          identifierType={identifierType}
          theme={theme}
        />
      )}

      {display === 'details' && (
        <Details
          metrics={metrics}
          data={data}
          config={config}
          printSize={plumprintSize}
          identifier={identifier}
          identifierType={identifierType}
          theme={theme}
        />
      )}

      {display === 'badge' && (
        <Badge
          metrics={metrics}
          data={data}
          config={config}
          printSize={plumprintSize}
          identifier={identifier}
          identifierType={identifierType}
          theme={theme}
        />
      )}
    </div>
  );
};

// Suspense boundary component that reads from the promise
const WidgetLoader = (props: WidgetProps) => {
  const data = use(
    fetchWidgetData({
      type: props.type,
      identifierType: props.identifierType,
      identifier: props.identifier,
      site: props.site,
      signal: props.signal,
    }),
  );

  return <WidgetContent {...props} data={data} />;
};

const MISSING_IDENTIFIER_CODE: LookupErrorCode = 'ARTIFACT_NOT_FOUND';

const Widget = (props: WidgetProps) => {
  // Blank/whitespace-only counts as missing, not just an empty string (FR-4.3).
  const isMissingIdentifier = !props.identifier?.trim();
  const { onEvent } = props;

  // Fire the host-facing event as a side effect — never during render, since
  // this branch returns before WidgetErrorBoundary and before any other hook
  // runs, and render can be double-invoked under StrictMode (AR-4.3).
  useEffect(() => {
    if (isMissingIdentifier) {
      onEvent('error', { code: MISSING_IDENTIFIER_CODE });
    }
  }, [isMissingIdentifier, onEvent]);

  if (isMissingIdentifier) {
    return <ErrorState message={getLookupErrorMessage(MISSING_IDENTIFIER_CODE)} />;
  }

  return (
    <WidgetErrorBoundary onEvent={props.onEvent}>
      <I18nProvider locale={resolveLanguage(props.lang)}>
        <Suspense fallback={<Skeleton />}>
          <WidgetLoader {...props} />
        </Suspense>
      </I18nProvider>
    </WidgetErrorBoundary>
  );
};

export default Widget;
